import React, { useContext, useState } from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import {
  Brain,
  FileText,
  TrendingUp,
  Rocket,
  Star,
  CheckCircle,
  Hourglass,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";

const ForDevelopersAISection = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <SectionWrapper id="ai-reviewer">
      <ContentWrapper>
        {/* LEFT CONTENT */}
        <LeftContent
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>
            For Developers — <Highlight>AI-Powered Resume Reviewer</Highlight>{" "}
            <ComingSoonTag>Coming Soon 🚀</ComingSoonTag>
          </Title>

          <Subtitle>
            Let AI analyze your resume, find improvement areas, and generate a
            step-by-step <Highlight>roadmap</Highlight> to reach your dream job
            & salary.
          </Subtitle>

          <FeatureList>
            <li>
              <Brain size={18} /> Smart Resume Scoring & Weakness Detection
            </li>
            <li>
              <FileText size={18} /> Personalized Resume Fix Suggestions
            </li>
            <li>
              <TrendingUp size={18} /> Roadmap to Reach Desired Salary
            </li>
            <li>
              <Rocket size={18} /> Skill Path Based on Your Target Role
            </li>
          </FeatureList>

          <ButtonWrapper>
            <CTAButton onClick={() => setShowComingSoon(true)}>
              Try AI Resume Reviewer
            </CTAButton>
            <InlineTag>Coming Soon</InlineTag>
          </ButtonWrapper>
        </LeftContent>

        {/* RIGHT VISUAL */}
        <RightVisual
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <AIBox
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 150 }}
          >
            <Header>
              <Brain size={22} color="#c59fff" />
              <h4>AI Resume Analysis</h4>
            </Header>

            <AIResult>
              <p>
                <Star size={14} color="#c59fff" /> Score:{" "}
                <strong>78 / 100</strong>
              </p>
              <ul>
                <li>
                  <CheckCircle size={14} color="#9b5cff" /> Missing measurable
                  impact metrics
                </li>
                <li>
                  <CheckCircle size={14} color="#9b5cff" /> Add recent tech
                  keywords (React 18, GraphQL)
                </li>
              </ul>
            </AIResult>
          </AIBox>

          <RoadmapCard
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 150 }}
          >
            <Header>
              <TrendingUp size={22} color="#c59fff" />
              <h4>AI Career Roadmap</h4>
            </Header>
            <ul>
              <li>📘 Master advanced React Native performance</li>
              <li>🧠 Practice DSA + System Design weekly</li>
              <li>🚀 Build 2 production-level apps</li>
              <li>💼 Target 30–35 LPA roles</li>
            </ul>
          </RoadmapCard>
        </RightVisual>
      </ContentWrapper>

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <ModalOverlay onClick={() => setShowComingSoon(false)}>
          <ModalBox
            as={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Hourglass size={38} color="#c59fff" />
            <h3>Coming Soon 🚀</h3>
            <p>
              The <Highlight>AI Resume Reviewer</Highlight> is on the way! Soon,
              you’ll get <Highlight>AI-powered resume feedback</Highlight>,
              personalized <Highlight>career roadmap</Highlight>, and even a
              downloadable AI-built resume! 💼✨
            </p>
            <ComingSoonButton onClick={() => setShowComingSoon(false)}>
              Got it 👍
            </ComingSoonButton>
          </ModalBox>
        </ModalOverlay>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </SectionWrapper>
  );
};

export default ForDevelopersAISection;

/* ---------------- Styled Components ---------------- */

const SectionWrapper = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #000, #0b0617);
  color: #fff;
  padding: 120px 40px;
  display: flex;
  justify-content: center;
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  gap: 40px;
`;

const LeftContent = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 550px;
`;

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1rem;
    color: #e0e0e0;
    svg {
      color: #c59fff;
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const glow = keyframes`
  0% { opacity: 0.6; transform: scale(1);}
  50% { opacity: 1; transform: scale(1.05);}
  100% { opacity: 0.6; transform: scale(1);}
`;

const InlineTag = styled.span`
  background: rgba(155, 92, 255, 0.2);
  color: #c59fff;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid rgba(155, 92, 255, 0.5);
  animation: ${glow} 2s infinite ease-in-out;
`;

const ComingSoonTag = styled(InlineTag)`
  font-size: 0.75rem;
  margin-left: 4px;
`;

const CTAButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  font-weight: 600;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.3);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.6);
  }
`;

const RightVisual = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 500px;
  position: relative;
  height: 500px;
`;

const AIBox = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 300px;
  backdrop-filter: blur(6px);
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.2);
`;

const RoadmapCard = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 40px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 280px;
  text-align: left;
  backdrop-filter: blur(6px);

  ul {
    list-style: none;
    padding: 0;
    margin-top: 10px;

    li {
      font-size: 0.9rem;
      color: #d2cde9;
      margin-bottom: 8px;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  h4 {
    font-size: 1.05rem;
    color: #fff;
    margin: 0;
  }
`;

const AIResult = styled.div`
  p {
    color: #c59fff;
    font-weight: 600;
    margin-bottom: 8px;
  }

  ul {
    list-style: none;
    padding-left: 0;
    color: #ccc;

    li {
      font-size: 0.9rem;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
`;

/* 🌟 Modal Styles */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ModalBox = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(155, 92, 255, 0.3);
  padding: 40px 30px;
  border-radius: 20px;
  max-width: 420px;
  text-align: center;
  box-shadow: 0 0 25px rgba(155, 92, 255, 0.3);
  color: #fff;

  h3 {
    margin: 16px 0 10px;
    font-size: 1.5rem;
  }

  p {
    font-size: 0.95rem;
    color: #d2cde9;
    line-height: 1.6;
  }
`;

const ComingSoonButton = styled.button`
  margin-top: 20px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }
`;
